/* global define, module */

/**
 * Create Konami Code Sequence recognition « Up Up Bottom Bottom Left Right Left Right B A » on specific HTMLElement or on global HTMLDocument. Usage of finger is also possible with « Up Up Bottom Bottom Left Right Left Right Tap Tap ».
 * @class KonamiCode
 * @param {Object|Function} [options]             - Container for all options. If type of `options` is Function, it is executed after Konami Code Sequence has been recognize.
 * @param {Function}        [options.callback]    - If `options` is not a Function, `options.callback` is executed after Konami Code Sequence has been entered. The first parameter provided by the callback is current instance of KonamiCode.
 * @param {Node}            [options.listener]    - By default it is the HTMLDocument `window.document`. You can pass some HTMLElement like `<input>` (HTMLInputElement) to only recognize Konami Code Sequence from this element.
 * @param {boolean}         [options.debug]       - By default it is set to `false`. When you set this value to `true`, that allows you to see all debug message in the console.
 */export class KonamiCode {
    private _input: string
    private _konamiCodeChar: string
    private _konamiCodeGesture: string
    private _originalCodeGesture: string
    private _startX: number
    private _startY: number
    private _stopX: number
    private _stopY: number
    private _tap: boolean
    private _capture: boolean
    private _listener: any
    private _afterCodeSequenceCallback: (self: KonamiCode) => void
    private _debug: null | ((msg: string, obj: any) => void)
    static _numberOfInstance = 0
  /**
   * Return the number of time KonamiCode was instanciated.
   * @function getNumberOfInstance
   * @memberOf KonamiCode.
   * @return {number} - Number of KonamiCode instance create from begining.
   */
  static getNumberOfInstance = () => {
      return KonamiCode._numberOfInstance;
  }

  /**
   * Active the listening of Konami Code Sequence.
   * @function enable
   * @memberOf KonamiCode#
   * @return {KonamiCode} Current instance of KonamiCode
   */
  public enable = () => {
      this._listenCodeCharSequence();
      this._listenCodeGestureSequence();
      this._debug && this._debug("Listener enabled for all.", "");

      return this;
  };

  /**
   * Active the listening of Konami Code Sequence for Keyboard Keys.
   * @function enableKeyboardKeys
   * @memberOf KonamiCode#
   * @return {KonamiCode} Current instance of KonamiCode
   */
  public enableKeyboardKeys = () => {
      this._listenCodeCharSequence();
      this._debug && this._debug("Listener enabled for Keyboard Keys.", "");

      return this;
  };

  /**
   * Active the listening of Konami Code Sequence for Touch Gesture.
   * @function enableTouchGesture
   * @memberOf KonamiCode#
   * @return {KonamiCode} Current instance of KonamiCode
   */
  public enableTouchGesture = () => {
      this._listenCodeGestureSequence();
      this._debug && this._debug("Listener enabled for Touch Gesture.", "");

      return this;
  };

  /**
   * Unactive the listening of Konami Code Sequence.
   * @function disable
   * @memberOf KonamiCode#
   * @return {KonamiCode} Current instance of KonamiCode
   */
  public disable = () => {
      this._stopCodeCharSequence();
      this._stopCodeGestureSequence();
      this._debug && this._debug("Listener disabled for all.", "");

      return this;
  };

  /**
   * Unactive the listening of Konami Code Sequence for Keyboard Keys.
   * @function disabledKeyboardKeys
   * @memberOf KonamiCode#
   * @return {KonamiCode} Current instance of KonamiCode
   */
  public disableKeyboardKeys = () => {
      this._stopCodeCharSequence();
      this._debug && this._debug("Listener disabled for Keyboard Keys.", "");

      return this;
  };

  /**
   * Unactive the listening of Konami Code Sequence for Touch Gesture.
   * @function disabledTouchGesture
   * @memberOf KonamiCode#
   * @return {KonamiCode} Current instance of KonamiCode
   */
  public disableTouchGesture = () => {
      this._stopCodeGestureSequence();
      this._debug && this._debug("Listener disabled for Touch Gesture.", "");

      return this;
  };

  /**
   * Change the listener. The old listener will no longer work. Note: change the listener enable this instance if it is previously disabled.
   * @function setListener
   * @param {Node} listener - You can pass some HTMLElement like `<input>` (HTMLInputElement) to only recognize Konami Code Sequence from this element.
   * @memberOf KonamiCode#
   * @return {KonamiCode} Current instance of KonamiCode
   */
  public setListener = (listener: any) => {
      this._stopCodeCharSequence();
      this._stopCodeGestureSequence();
      this._listener = listener || document;
      this._listenCodeCharSequence();
      this._listenCodeGestureSequence();
      this._debug && this._debug("Listener changed.", listener);

      return this;
  };

  /**
   * Change the Function executed after Konami Code Sequence has been entered.
   * @function setCallback
   * @param {Function} callback - Function executed after Konami Code Sequence has been entered. The first parameter provided by the callback is current instance of KonamiCode.
   * @memberOf KonamiCode#
   * @return {KonamiCode} Current instance of KonamiCode
   * @example new KonamiCode().setCallback(function (konamiCode) {
   *     konamiCode.disable();
   *     // Do something here.
   * });
   */
  public setCallback = (callback: () => void) => {
      this._afterCodeSequenceCallback = (typeof callback === "function" && callback) || this._defaultCallback;
      this._debug && this._debug("Callback changed.", callback);

      return this;
  };

  private _keptLastCodeChar = () => {
      if (this._input.length > this._konamiCodeChar.length) {
          this._input = this._input.substr((this._input.length - this._konamiCodeChar.length));
      }
  };

  private _defaultCallback = () => {
      this._debug && this._debug("Konami Code Sequence Entered. There is no action defined.", "");
  };

  private _checkIfCodeCharIsValid = () => {
      if (this._input === this._konamiCodeChar) {
          this._afterCodeSequenceCallback(this);
      }
  };

  private _codeSequenceEventKeyDown = (event: KeyboardEvent) => {
      this._input += event.keyCode;
      this._keptLastCodeChar();
      this._checkIfCodeCharIsValid();
  };

  private _codeSequenceEventTouchMove = (event: TouchEvent) => {
      let touch;
      if (event.touches.length === 1 && this._capture === true) {
          touch = event.touches[0];
          this._stopX = touch.pageX;
          this._stopY = touch.pageY;
          this._tap = false;
          this._capture = false;
          this._checkIfCodeGestureIsValid();
      }
  };

  private _codeSequenceEventTouchEnd = () => {
      if (this._tap === true) {
          this._checkIfCodeGestureIsValid();
      }
  };

  private _codeSequenceEventTouchStart = (event: TouchEvent) => {
      this._startX = event.changedTouches[0].pageX;
      this._startY = event.changedTouches[0].pageY;
      this._tap = true;
      this._capture = true;
  };

  private _stopCodeCharSequence = () => {
      this._listener.removeEventListener("keydown", this._codeSequenceEventKeyDown);
  };

  private _stopCodeGestureSequence = () => {
      this._listener.removeEventListener("touchstart", this._codeSequenceEventTouchStart);
      this._listener.removeEventListener("touchmove", this._codeSequenceEventTouchMove);
      this._listener.removeEventListener("touchend", this._codeSequenceEventTouchEnd);
  };

  private _listenCodeCharSequence = () => {
      this._stopCodeCharSequence();
      this._listener.addEventListener("keydown", this._codeSequenceEventKeyDown);
  };

  private _listenCodeGestureSequence = () => {
      this._originalCodeGesture = this._konamiCodeGesture;
      this._stopCodeGestureSequence();
      this._listener.addEventListener("touchstart", this._codeSequenceEventTouchStart);
      this._listener.addEventListener("touchmove", this._codeSequenceEventTouchMove);
      this._listener.addEventListener("touchend", this._codeSequenceEventTouchEnd, false);
  };

  private _checkIfCodeGestureIsValid = () => {
     const xMagnitude = Math.abs(this._startX - this._stopX),
          yMagnitude = Math.abs(this._startY - this._stopY),
          x = (this._startX - this._stopX < 0) ? "rt" : "lt",
          y = (this._startY - this._stopY < 0) ? "dn" : "up";
      let result = (xMagnitude > yMagnitude) ? x : y;

      result = (this._tap === true) ? "tp" : result;

      if (result === this._konamiCodeGesture.substr(0, 2)) {
          this._konamiCodeGesture = this._konamiCodeGesture.substr(2, this._konamiCodeGesture.length - 2);
      } else {
          this._konamiCodeGesture = this._originalCodeGesture;
      }

      if (this._konamiCodeGesture.length === 0) {
          this._konamiCodeGesture = this._originalCodeGesture;
          this._afterCodeSequenceCallback(this);
      }
  };

  private _checkDebugMode = (options: any) => {
      if (options && options.debug === true) {
          this._debug = function (message, obj = null) {
              if (obj !== undefined) {
                  // eslint-disable-next-line no-console
                  console.log(message, obj);
              } else {
                  // eslint-disable-next-line no-console
                  console.log(message);
              }
          };
          this._debug && this._debug("Debug Mode On.", "");
      } else {
          this._debug = null;
      }
  };

  constructor(options: any) {
      this._input = "";
      this._konamiCodeChar = "38384040373937396665";
      this._konamiCodeGesture = "upupdndnltrtltrttptp";
      this._originalCodeGesture = ""
      this._startX = 0;
      this._startY = 0;
      this._stopX = 0;
      this._stopY = 0;
      this._tap = false;
      this._capture = false;
      this._debug = null;

      KonamiCode._numberOfInstance = (KonamiCode._numberOfInstance) ? KonamiCode._numberOfInstance + 1 : 1;

      this._checkDebugMode(options);
      this._listener = (options && options.listener) || document;
      this._afterCodeSequenceCallback =
          (typeof options === "function" && options) ||
          (options && typeof options.callback === "function" && options.callback) ||
          this._defaultCallback;
      this._listenCodeCharSequence();
      this._listenCodeGestureSequence();
  }
}